import moment from 'moment';
import React from 'react';
import intJson from 'config/task-types/int.json';
import {
  reduceStatusesWithSubstatusesToArray,
  sortWithOtherComparer,
} from 'services/utils/task-service';
import { TherapyUtil } from 'utils/therapy-util';
import {
  INTERVENTION_TYPES,
  INTERVENTION_SEVERITIES,
  INTERVENTION_REASON_TYPE,
} from 'constants/index';
import { InterventionStatuses } from 'constants/intervention-statuses';
import { interventionTypeRelatedFields } from 'constants/lists';
import { MedicationStatus } from 'constants/enums';
import { addMedsIntoMedGroups } from 'services/utils/medication-service';
import LabHistory from 'components/dynamic-form/custom-elements/lab-history';
// eslint-disable-next-line max-len
import InterventionCopyContentButton from 'components/intervention-button/intervention-copy-content-button';
import { InterventionSeverityBanner } from 'components/intervention-severity-banner/intervention-severity-banner';
import {
  DISEASE_PROGRESSION_OPTIONS,
  VISIT_TYPE_OPTIONS,
} from 'constants/intervention-hospitalization-options';
import { staticProviders, getCommonProviders, getDateForProvider } from './common-provider';
import { InterventionTypeId } from '../../../interfaces/enums/InterventionType';

const MIGRATED_INT_TYPES = [
  INTERVENTION_TYPES.ADHERENCE,
  INTERVENTION_TYPES.ADR,
  INTERVENTION_TYPES.DRUG_INFORMATION,
  INTERVENTION_TYPES.INFECTION_CONTROL,
  INTERVENTION_TYPES.PRODUCT_ISSUE,
  INTERVENTION_TYPES.REGIMEN,
];

// INT (reason, recommendation) must be previously configured in the `lookup_task_interventions_reason_recommendations` table
const DYNAMIC_INT_RECOMMENDATION_SUPPORTED = {
  [INTERVENTION_TYPES.REGIMEN]: {
    intJsonFieldName: 'reason_single',
    reasonArrFieldId: 'id',
    lookupFieldName: 'reason_id',
    formValueFieldName: 'reason',
  },
  [INTERVENTION_TYPES.ADHERENCE]: {
    intJsonFieldName: 'reason_type_adherence',
    reasonArrFieldId: 'reason_subtype',
    lookupFieldName: 'reason_subtype',
    formValueFieldName: 'reason_type',
  },
};

const MIGRATED_INT_TYPES_WITHOUT_REASON = [INTERVENTION_TYPES.INFECTION_CONTROL];

const mapToIdName = (id, name, showDisabledOptions) => it => {
  if (showDisabledOptions) {
    return {
      name: it[name],
      id: it[id],
      disabled: !it.is_active,
    };
  }
  return {
    name: it[name],
    id: it[id],
  };
};

const createDropdownItemList = (list, id, name, filterId, filterName, showDisabledOpts) => {
  let newFilter = list;
  if (filterId) {
    newFilter = list.filter(r => r[filterId] === filterName);
  }
  newFilter.sort(sortWithOtherComparer(name));
  return newFilter.map(mapToIdName(id, name, showDisabledOpts));
};

let intDataMapping = {};
let reasonTypeCleared = false; // Track if reason_type was cleared for manual interventions

export default function (task, state) {
  let { taskStatuses } = state;
  let medicationGroups;
  let interactionMedicationGroups;
  // eslint-disable-next-line camelcase
  let taskTypeId = state.formValues?.type_id;
  // eslint-disable-next-line camelcase
  if (state.formValues?.intervention_json?.values) {
    // eslint-disable-next-line camelcase
    taskTypeId = state.formValues?.intervention_json?.values.type_id;
  }
  if (!taskTypeId) {
    // eslint-disable-next-line camelcase
    taskTypeId = state.data?.type_id;
  }
  if (!taskTypeId && task) {
    taskTypeId = task.type_id;
  }

  const initializeDataMapping = () => {
    if (task.protocol) {
      const { taskId } = task;
      const { clinicalDataTypeId } = task.protocol;
      const key = `${taskId}-${clinicalDataTypeId}`;

      if (!intDataMapping[key]) {
        intDataMapping[key] = {
          intType: taskTypeId,
          selectedReason: task.reason,
          recommendations: [],
          reasonTypeCleared: false,
        };
      }
    }
    return intDataMapping;
  };

  if (state.firstRender) {
    intDataMapping = {};
    reasonTypeCleared = false;
  }
  initializeDataMapping();

  const tempDataMappingKey =
    task?.taskId && task?.protocol?.clinicalDataTypeId
      ? `${task.taskId}-${task.protocol.clinicalDataTypeId}`
      : null;

  const interventionType = typeId =>
    // eslint-disable-next-line camelcase
    taskStatuses.intervention_types.find(type => type.id === typeId)?.type_name;

  if (!taskStatuses || !taskStatuses.intervention_categories) {
    taskStatuses = {
      intervention_categories: [],
      intervention_severities: [],
      intervention_types: [],
      intervention_recommendations: [],
      intervention_reasons: [],
      intervention_outcomes: [],
    };
  }
  const recommendationArr = [
    ...taskStatuses.intervention_recommendations.filter(
      rec => String(rec.type_id) === String(taskTypeId),
    ),
  ];

  const reasonArr = taskStatuses.intervention_reasons.filter(
    rec => String(rec.type_id) === String(taskTypeId),
  );
  const reasonTypeArr = taskStatuses.intervention_reasons;
  const outcomeArr = taskStatuses.intervention_outcomes.filter(
    rec => String(rec.type_id) === String(taskTypeId),
  );

  const commonProviders = getCommonProviders(task, state, intJson);
  let initialReasonType = '';
  const mappingData = tempDataMappingKey ? intDataMapping[tempDataMappingKey] : null;

  // Only initialize from task.reason if it hasn't been explicitly cleared
  // Check both protocol-based (mappingData) and manual intervention (reasonTypeCleared) cases
  const wasCleared = mappingData?.reasonTypeCleared || reasonTypeCleared;
  if (task.reason && !wasCleared) {
    const id = String(task.reason).split(',')[0];
    initialReasonType = (reasonArr.find(r => String(r.id) === id) || {}).reason_subtype;
  }
  const nowDate = getDateForProvider(moment());

  if (state.medications) {
    const { medicationList, medicationGroups: overwriteGroups } = state.medications;
    const fullMedicationGroups = addMedsIntoMedGroups(overwriteGroups, medicationList);

    medicationGroups = fullMedicationGroups.map(group => ({
      name: group.header.medication,
      id: group.header.medication,
    }));

    const completedInt =
      task.status_id !== null &&
      [
        InterventionStatuses.Canceled,
        InterventionStatuses.Completed,
        InterventionStatuses.Canceled_PatientDeceased,
      ].includes(Number(task.status_id));

    const interactingMedicationsGpis =
      task?.interaction?.concepts
        ?.filter(concept => concept.fdb_concept_type_id === 1)
        .map(concept => concept.gpi) || [];

    interactionMedicationGroups = fullMedicationGroups
      .filter(group => interactingMedicationsGpis.includes(group.header.gpi))
      .map(group => ({
        name: group.header.medication,
        id: group.header.id,
        disabled:
          completedInt ||
          (completedInt === false &&
            (group.header.status_code !== MedicationStatus.Active || !!group.header.therapy_id)),
      }));
  }

  let updatedFields = [];

  const handleFields = checkboxValue => {
    const fieldsToUpdate = ['admission_date_completed', 'discharged_date_completed'];
    const listOfFields = intJson.fields.map(item => {
      if (fieldsToUpdate.includes(item.id)) {
        item.required = !checkboxValue;
      }
      return item;
    });
    updatedFields = updatedFields.concat(listOfFields);
  };

  const getRightOutComeId = currentIntType => {
    switch (currentIntType) {
      case InterventionTypeId.Dur:
      case InterventionTypeId.Adr:
      case InterventionTypeId.Regimen:
      case InterventionTypeId.ReferralOfService:
      case InterventionTypeId.OtherClinical:
      case InterventionTypeId.LinkageToCare:
      case InterventionTypeId.ProductIssue:
      case InterventionTypeId.DrugInformation:
      case InterventionTypeId.Vaccine:
      case InterventionTypeId.InfectionControl:
      case InterventionTypeId.HospitalizationAndEr:
        return 'outcome';
      case InterventionTypeId.Adherence:
        return 'outcome_type_adherence';
      case InterventionTypeId.Lab:
        return 'outcome_multiple';
      default:
        return null;
    }
  };

  const dynamicallySetFieldsAsRequiredOrNot = (fieldsToUpdate, valueForRequiredProp) => {
    const listOfFields = intJson.fields.map(item => {
      if (fieldsToUpdate.includes(item.id)) {
        item.required = valueForRequiredProp;
      }
      return item;
    });
    updatedFields = updatedFields.concat(listOfFields);
  };

  const updateFieldsAsRequiredOrnot = (currentOutcomeValue, currentIntType) => {
    switch (currentIntType) {
      case InterventionTypeId.Regimen:
      case InterventionTypeId.Adr:
        const fieldsToUpdate1 = ['category', 'service_group', 'details', 'reason_single'];
        if (currentOutcomeValue) {
          dynamicallySetFieldsAsRequiredOrNot(fieldsToUpdate1, true);
        } else {
          dynamicallySetFieldsAsRequiredOrNot(fieldsToUpdate1, false);
        }
        break;
      case InterventionTypeId.Dur:
      case InterventionTypeId.ProductIssue:
      case InterventionTypeId.DrugInformation:
      case InterventionTypeId.Vaccine:
      case InterventionTypeId.InfectionControl:
        const fieldsToUpdate2 = ['category', 'service_group', 'details'];
        if (currentOutcomeValue) {
          dynamicallySetFieldsAsRequiredOrNot(fieldsToUpdate2, true);
        } else {
          dynamicallySetFieldsAsRequiredOrNot(fieldsToUpdate2, false);
        }
        break;
      case InterventionTypeId.OtherClinical:
        const fieldsToUpdate3 = ['category', 'service_group', 'details', 'additional_reason_other'];
        if (currentOutcomeValue) {
          dynamicallySetFieldsAsRequiredOrNot(fieldsToUpdate3, true);
        } else {
          dynamicallySetFieldsAsRequiredOrNot(fieldsToUpdate3, false);
        }
        break;
      case InterventionTypeId.ReferralOfService:
        const fieldsToUpdate4 = ['category', 'service_group', 'details', 'reason_all'];
        if (currentOutcomeValue) {
          dynamicallySetFieldsAsRequiredOrNot(fieldsToUpdate4, true);
        } else {
          dynamicallySetFieldsAsRequiredOrNot(fieldsToUpdate4, false);
        }
        break;
      case InterventionTypeId.LinkageToCare:
        const fieldsToUpdate5 = [
          'category',
          'service_group',
          'details',
          'reason_single_linkage_to_care_hiv',
          'reason_single_linkage_to_care_pep_prep',
          'reason_single_linkage_to_care_hcv',
          'reason_single_linkage_to_care_oncology',
          'reason_single_linkage_to_care_neurology',
          'reason_single_linkage_to_care_dermatology',
          'reason_single_linkage_to_care_gi',
          'reason_single_linkage_to_care_rhemautology',
          'reason_single_linkage_to_care_diabetes',
          'reason_single_linkage_to_care_endocrinology',
          'reason_single_linkage_to_care_cardiology',
          'reason_single_linkage_to_care_weight_management',
          'reason_single_linkage_to_care_hbv',
        ]; // reason_type_linkage_to_care
        if (currentOutcomeValue) {
          dynamicallySetFieldsAsRequiredOrNot(fieldsToUpdate5, true);
        } else {
          dynamicallySetFieldsAsRequiredOrNot(fieldsToUpdate5, false);
        }
        break;
      case InterventionTypeId.Adherence:
        const fieldsToUpdate6 = ['category', 'service_group', 'details']; // reason_type_adherence
        if (currentOutcomeValue) {
          dynamicallySetFieldsAsRequiredOrNot(fieldsToUpdate6, true);
        } else {
          dynamicallySetFieldsAsRequiredOrNot(fieldsToUpdate6, false);
        }
        break;
      case InterventionTypeId.Lab:
        const fieldsToUpdate7 = ['category', 'service_group', 'details', 'reason_all'];
        if (currentOutcomeValue) {
          dynamicallySetFieldsAsRequiredOrNot(fieldsToUpdate7, true);
        } else {
          dynamicallySetFieldsAsRequiredOrNot(fieldsToUpdate7, false);
        }
        break;
      default:
        break;
    }
  };

  // Helper to update intDataMapping with new recommendation data
  const updateDataMapping = ({
    selectedReason,
    recommendations,
    intType = taskTypeId,
    selectedReasonType,
  }) => {
    if (tempDataMappingKey && intDataMapping[tempDataMappingKey]) {
      intDataMapping[tempDataMappingKey] = {
        ...intDataMapping[tempDataMappingKey],
        intType,
        selectedReason,
        recommendations,
        selectedReasonType,
      };
    }
  };

  // Helper to filter and update recommendations based on reason
  const getFilteredRecommendations = reasonId => {
    const fieldId =
      DYNAMIC_INT_RECOMMENDATION_SUPPORTED[taskTypeId]?.lookupFieldName || 'reason_id';
    const tempRecommendationList =
      state.lookups.lookupTaskInterventionsReasonRecommendations.filter(
        r => r[fieldId] == reasonId,
      );

    if (!tempRecommendationList?.length) return null;

    const recommendationStatusMap = new Map(
      tempRecommendationList.map(r => [r.recommendation_id, r.is_active]),
    );

    const filteredRecommendations = recommendationArr
      .filter(rec => recommendationStatusMap.has(rec.id))
      .map(rec => {
        const activeStatus = recommendationStatusMap.get(rec.id);
        return activeStatus !== rec.is_active ? { ...rec, is_active: activeStatus } : rec;
      });

    return createDropdownItemList(
      filteredRecommendations,
      'id',
      'recommendation_name',
      null,
      null,
      true,
    );
  };

  const getIntJsonFieldName = () => {
    return DYNAMIC_INT_RECOMMENDATION_SUPPORTED[taskTypeId]?.intJsonFieldName || 'reason_single';
  };

  const getFormValueFieldName = () => {
    return DYNAMIC_INT_RECOMMENDATION_SUPPORTED[taskTypeId]?.formValueFieldName || 'reason';
  };

  const getReasonArrFieldId = () => {
    return DYNAMIC_INT_RECOMMENDATION_SUPPORTED[taskTypeId]?.reasonArrFieldId || 'id';
  };

  const handleChange = (fieldId, value, formValues) => {
    switch (fieldId) {
      case 'type': {
        const formId = state.formId || state.meta?.form;
        interventionTypeRelatedFields.forEach(fieldName => {
          if (formValues[fieldName]) {
            state.change(formId, fieldName, null);
          }
        });
        const currentOutcomeIdToUse = getRightOutComeId(value) ?? '';
        const rightOutcomeValue = currentOutcomeIdToUse ? formValues[currentOutcomeIdToUse] : null;
        updateFieldsAsRequiredOrnot(rightOutcomeValue, value);
        state.change(formId, 'type_id', value);
        // Mark reason_type as cleared so it doesn't get re-initialized from task.reason
        if (tempDataMappingKey && intDataMapping[tempDataMappingKey]) {
          // Protocol-based intervention
          intDataMapping[tempDataMappingKey].reasonTypeCleared = true;
        } else {
          // Manual intervention (no protocol)
          reasonTypeCleared = true;
        }

        if (state?.data?.protocol?.clinicalDataTypeId) {
          updateDataMapping({
            selectedReason: null,
            recommendations: [],
            intType: value,
            selectedReasonType: null,
          });
        }
        return handleChange('reason', null, formValues);
      }
      case 'outcome_type_adherence':
        if (value === 'Requires Follow Up') {
          state.change(state.formId, 'outcome', 1003);
        }
        return updateFieldsAsRequiredOrnot(value, formValues.type_id);
      case 'no_therapy_ids_checkbox':
        return state.change(state.formId, 'therapy_ids', null);
      case 'unknown_admission_discharge_date':
        return handleFields(value.target.checked);
      case 'outcome':
      case 'outcome_multiple':
        return updateFieldsAsRequiredOrnot(value, formValues?.type_id);
      case 'reason_single': {
        // Check if this intervention type supports dynamic recommendations
        const shouldProcessRecommendations =
          DYNAMIC_INT_RECOMMENDATION_SUPPORTED[taskTypeId] &&
          getIntJsonFieldName() === 'reason_single' &&
          recommendationArr?.length &&
          state?.lookups?.lookupTaskInterventionsReasonRecommendations?.length;

        if (!shouldProcessRecommendations) {
          // For types that don't support dynamic filtering, use all recommendations for that type
          // Only populate recommendations if a reason is selected
          if (!value) {
            if (state?.data?.protocol?.clinicalDataTypeId) {
              updateDataMapping({ selectedReason: null, recommendations: [] });
            } else if (!state?.data?.protocol) {
              return [];
            }
            return [];
          }

          const activeRecommendations = recommendationArr.filter(rec => rec.is_active);
          const allRecommendations = createDropdownItemList(
            activeRecommendations,
            'id',
            'recommendation_name',
          );
          if (state?.data?.protocol?.clinicalDataTypeId) {
            updateDataMapping({ selectedReason: value, recommendations: allRecommendations });
          } else if (!state?.data?.protocol) {
            return allRecommendations;
          }
          return [];
        }

        // Check if the selected reason matches the current intervention type
        const selectedReason = (state?.taskStatuses?.intervention_reasons || []).find(
          ir => ir.id == value,
        );
        const isValidReason = selectedReason?.type_id == taskTypeId;

        if (isValidReason) {
          const masterDetailRecommendationList = getFilteredRecommendations(value);

          if (masterDetailRecommendationList) {
            if (state?.data?.protocol?.clinicalDataTypeId) {
              updateDataMapping({
                selectedReason: value,
                recommendations: masterDetailRecommendationList,
              });
            } else if (!state?.data?.protocol) {
              return masterDetailRecommendationList;
            }
          }
        } else {
          // Reset recommendations if reason doesn't match intervention type
          updateDataMapping({ selectedReason: null, recommendations: [] });
        }

        return [];
      }
      case 'reason_type_adherence': {
        // Check if this intervention type supports dynamic recommendations
        const shouldProcessRecommendations =
          DYNAMIC_INT_RECOMMENDATION_SUPPORTED[taskTypeId] &&
          recommendationArr?.length &&
          state?.lookups?.lookupTaskInterventionsReasonRecommendations?.length;

        if (!shouldProcessRecommendations) {
          return null;
        }

        let cleanValue = value;
        if (String(value).includes(',')) {
          const id = String(value).split(',')[0];
          cleanValue = (reasonArr.find(r => String(r.id) === id) || {}).reason_subtype;
        }

        const masterDetailRecommendationList = getFilteredRecommendations(cleanValue);

        if (masterDetailRecommendationList) {
          if (state?.data?.protocol?.clinicalDataTypeId) {
            updateDataMapping({
              recommendations: masterDetailRecommendationList,
              selectedReasonType: value,
            });
          } else if (!state?.data?.protocol) {
            return masterDetailRecommendationList;
          }
        }

        return [];
      }
      default:
        return null;
    }
  };

  const getDynamicRecommendationData = () => {
    // For types that don't support dynamic filtering or don't have a reason dependency, use all active recommendations for that type
    if (
      !MIGRATED_INT_TYPES.includes(taskTypeId) ||
      MIGRATED_INT_TYPES_WITHOUT_REASON.includes(taskTypeId)
    ) {
      return createDropdownItemList(
        recommendationArr.filter(r => r.is_active),
        'id',
        'recommendation_name',
      );
    }

    // Get the data mapping for the current task/protocol
    const mostRecentValues = tempDataMappingKey ? intDataMapping[tempDataMappingKey] : null;
    const selectedReason = mostRecentValues?.selectedReason || mostRecentValues?.selectedReasonType;
    const reasonValue = state?.formValues?.[getFormValueFieldName()] || selectedReason;
    const reasonFieldId = getIntJsonFieldName();

    if (!reasonValue) {
      return [];
    }

    // Manual Interventions
    if (!state?.data?.protocol) {
      if (reasonValue && !reasonArr.find(r => r[getReasonArrFieldId()] == reasonValue)) {
        return [];
      }

      // Always call handleChange to get fresh recommendations for the selected reason
      // This ensures recommendations update when the reason changes
      const result = handleChange(reasonFieldId, reasonValue, state?.formValues);
      return result || [];
    }

    // Protocol Interventions
    if (state?.data?.protocol?.clinicalDataTypeId) {
      if (!mostRecentValues?.selectedReason && !reasonValue) {
        return [];
      }
      if (mostRecentValues?.selectedReason) {
        handleChange(reasonFieldId, mostRecentValues.selectedReason, state?.formValues);
        return mostRecentValues?.recommendations ?? [];
      }
      if (!mostRecentValues?.selectedReason && reasonValue) {
        handleChange(reasonFieldId, reasonValue, state?.formValues);
        return mostRecentValues?.recommendations ?? [];
      }
    }
    return [];
  };

  const therapies =
    state && state.therapies
      ? Object.values(state?.therapies).map(therapy => ({
          ...therapy,
          // eslint-disable-next-line max-len
          drug_name_with_dosage: TherapyUtil.therapyFormatDosage(therapy),
        }))
      : [];

  if (state?.formValues?.unknown_admission_discharge_date) {
    handleFields(true);
  } else if (!state?.formValues?.unknown_admission_discharge_date) {
    handleFields(false);
  }

  if (state?.firstRender) {
    updateFieldsAsRequiredOrnot('', taskTypeId);
  }

  // Helper to check if the intervention is Adherence with Proactive reason type
  const getReasonSubtype = () => {
    // Check form values first
    if (state?.formValues?.reason_type) {
      return state.formValues.reason_type;
    }

    // Fall back to data mapping
    const mappedData = intDataMapping[tempDataMappingKey];
    if (mappedData?.selectedReasonType) {
      return mappedData.selectedReasonType;
    }

    return null;
  };

  const isAdherenceType =
    state?.formValues?.type_id === INTERVENTION_TYPES.ADHERENCE ||
    intDataMapping[tempDataMappingKey]?.intType === INTERVENTION_TYPES.ADHERENCE;

  const isAdherenceProactive =
    isAdherenceType && getReasonSubtype() === INTERVENTION_REASON_TYPE.PROACTIVE;

  const getOutcomesAdherence = () => {
    const mostRecentValues = tempDataMappingKey ? intDataMapping[tempDataMappingKey] : null;
    const reasonTypeValue =
      state?.formValues?.[getFormValueFieldName()] || mostRecentValues?.selectedReasonType;

    if (!reasonTypeValue) {
      return [];
    }

    // 1010 - Pharmacist Completed
    // 1012 - Follow up task created
    const proactiveOutcomeIds = [1010, 1012];
    if (isAdherenceProactive) {
      const filteredOutcomes = outcomeArr.filter(outcome =>
        proactiveOutcomeIds.includes(outcome.id),
      );
      return createDropdownItemList(filteredOutcomes, 'id', 'outcome_name');
    }

    return createDropdownItemList(outcomeArr, 'id', 'outcome_name');
  };

  return {
    ...staticProviders,
    ...commonProviders,
    json: {
      ...intJson,
      fields: updatedFields.length ? [...updatedFields] : [...intJson.fields],
    },
    events: {
      type: {
        onChange: (field, value, formValues) => {
          handleChange(field.id, value, formValues);
        },
      },
      outcome_type_adherence: {
        onChange: (field, value, formValues) => {
          handleChange(field.id, value, formValues);
        },
      },
      no_therapy_ids_checkbox: {
        onChange: (field, value, formValues) => {
          handleChange(field.id, value, formValues);
        },
      },
      unknown_admission_discharge_date: {
        onChange: (field, value, formValues) => {
          handleChange(field.id, value, formValues);
        },
      },
      outcome: {
        onChange: (field, value, formValues) => {
          handleChange(field.id, value, formValues);
        },
      },
      outcome_multiple: {
        onChange: (field, value, formValues) => {
          handleChange(field.id, value, formValues);
        },
      },
      reason_single: {
        onChange: (field, value, formValues) => {
          handleChange(field.id, value, formValues);
        },
      },
      reason_type_adherence: {
        onChange: (field, value, formValues) => {
          handleChange(field.id, value, formValues);
        },
      },
    },
    therapies: createDropdownItemList(therapies, 'id', 'drug_name_with_dosage'),
    categories: createDropdownItemList(taskStatuses.intervention_categories, 'id', 'category_name'),
    severities: createDropdownItemList(taskStatuses.intervention_severities, 'id', 'severity_name'),
    visitType: createDropdownItemList(VISIT_TYPE_OPTIONS, 'value', 'label'),
    diseaseProgression: createDropdownItemList(DISEASE_PROGRESSION_OPTIONS, 'value', 'label'),
    types: createDropdownItemList(taskStatuses.intervention_types, 'id', 'type_name'),
    reasons: createDropdownItemList(reasonArr, 'id', 'reason_name'),
    infectionTypes: createDropdownItemList(
      state?.lookups?.lookupInterventionInfectionTypes ?? [],
      'id',
      'type_name',
    ),
    recommendedAppropriateVaccines: state?.lookups?.lookupRecommendedAppropriateVaccines,
    reasonsLinkageToCareHIV: createDropdownItemList(
      reasonTypeArr,
      'id',
      'reason_name',
      'reason_subtype',
      'HIV',
    ).filter(status => status.name),
    reasonsLinkageToCarePEPPrEP: createDropdownItemList(
      reasonTypeArr,
      'id',
      'reason_name',
      'reason_subtype',
      'PEP/PrEP',
    ).filter(status => status.name),
    reasonsLinkageToCareHCV: createDropdownItemList(
      reasonTypeArr,
      'id',
      'reason_name',
      'reason_subtype',
      'HCV',
    ).filter(status => status.name),
    reasonsLinkageToCareOncology: createDropdownItemList(
      reasonTypeArr,
      'id',
      'reason_name',
      'reason_subtype',
      'Oncology',
    ).filter(status => status.name),
    reasonsLinkageToCareNeurology: createDropdownItemList(
      reasonTypeArr,
      'id',
      'reason_name',
      'reason_subtype',
      'Neurology',
    ).filter(status => status.name),
    reasonsLinkageToCareDermatology: createDropdownItemList(
      reasonTypeArr,
      'id',
      'reason_name',
      'reason_subtype',
      'Dermatology',
    ).filter(status => status.name),
    reasonsLinkageToCareGI: createDropdownItemList(
      reasonTypeArr,
      'id',
      'reason_name',
      'reason_subtype',
      'GI',
    ).filter(status => status.name),
    reasonsLinkageToCareRhemautology: createDropdownItemList(
      reasonTypeArr,
      'id',
      'reason_name',
      'reason_subtype',
      'Rhemautology',
    ).filter(status => status.name),
    reasonsLinkageToCareDiabetes: createDropdownItemList(
      reasonTypeArr,
      'id',
      'reason_name',
      'reason_subtype',
      'Diabetes',
    ).filter(status => status.name),
    reasonsLinkageToCareEndocrinology: createDropdownItemList(
      reasonTypeArr,
      'id',
      'reason_name',
      'reason_subtype',
      'Endocrinology',
    ).filter(status => status.name),
    reasonsLinkageToCareCardiology: createDropdownItemList(
      reasonTypeArr,
      'id',
      'reason_name',
      'reason_subtype',
      'Cardiology',
    ).filter(status => status.name),
    reasonsLinkageToCareWeightManagement: createDropdownItemList(
      reasonTypeArr,
      'id',
      'reason_name',
      'reason_subtype',
      'Weight Management',
    ).filter(status => status.name),
    reasonsLinkageToCareHBV: createDropdownItemList(
      reasonTypeArr,
      'id',
      'reason_name',
      'reason_subtype',
      'HBV',
    ).filter(status => status.name),
    reasonsIntentional: createDropdownItemList(
      reasonTypeArr,
      'id',
      'reason_name',
      'reason_subtype',
      'Intentional',
    ),
    reasonsUnintentional: createDropdownItemList(
      reasonTypeArr,
      'id',
      'reason_name',
      'reason_subtype',
      'Unintentional',
    ),
    reasonsProactive: createDropdownItemList(
      reasonTypeArr,
      'id',
      'reason_name',
      'reason_subtype',
      'Proactive',
    ),
    reasonsCheckin: createDropdownItemList(
      reasonTypeArr,
      'id',
      'reason_name',
      'reason_subtype',
      'Check In',
    ),
    reasonsDescriptionOfIssue: createDropdownItemList(
      reasonTypeArr,
      'id',
      'reason_name',
      'reason_subtype',
      'Description of Issue',
    ),
    reasonsAll: reasonArr.filter(r => !r.reason_subtype).map(mapToIdName('id', 'reason_name')),
    reasonTypesAdherence: createDropdownItemList(
      reduceStatusesWithSubstatusesToArray(
        reasonTypeArr,
        'reason_subtype',
        INTERVENTION_TYPES.ADHERENCE,
      ),
      'value',
      'label',
    ),
    reasonTypesOtherClinical: createDropdownItemList(
      reduceStatusesWithSubstatusesToArray(
        reasonTypeArr,
        'reason_subtype',
        INTERVENTION_TYPES.OTHER_CLINICAL,
      ),
      'value',
      'label',
    ),
    reasonTypesLinkageToCare: createDropdownItemList(
      reduceStatusesWithSubstatusesToArray(
        reasonTypeArr,
        'reason_subtype',
        INTERVENTION_TYPES.LINKAGE_TO_CARE,
      ),
      'value',
      'label',
    ),
    outcomes: createDropdownItemList(outcomeArr, 'id', 'outcome_name'),
    outcomesAdherence: getOutcomesAdherence(),
    interactingMeds: interactionMedicationGroups,
    medicationGroups,
    customElements: [
      {
        id: 'lab-history',
        content: LabHistory,
      },
      {
        id: 'intervention-copy-content-button',
        content: () => {
          const formValues =
            state.formValues && state.formValues.intervention_json
              ? state.formValues.intervention_json.values
              : null;
          return (
            <InterventionCopyContentButton
              interventionType={formValues ? interventionType(formValues.type_id) : null}
              details={formValues ? formValues.details : null}
            />
          );
        },
      },
      {
        id: 'intervention-severity-banner',
        content: () => {
          const { severity_id } = state.formValues || {};

          return severity_id && <InterventionSeverityBanner severity_id={severity_id} />;
        },
      },
    ],
    recommendations: getDynamicRecommendationData(),
    initialValues: {
      reason_type: initialReasonType,
      in_progress_date: nowDate,
      completed_date: nowDate,
      canceled_dt: nowDate,
      followup_dt: state.currentDateTime,
      severity_id: null,
      no_therapy_ids_checkbox:
        task.status_id === InterventionStatuses.Completed && !task.therapy_ids,
      therapy_id: task.therapy_id || null,
      therapy_ids_inactive: task.therapy_ids_inactive || null,
      serious_adverse_event: task.serious_adverse_event,
      infection_type_id: task?.infection_type_id ?? null,
    },
    leftAlignedButtons: [
      {
        button: (
          <InterventionCopyContentButton
            interventionType={state.formValues ? interventionType(state.formValues.type_id) : null}
            details={state.formValues ? state.formValues.details : null}
          />
        ),
      },
    ],
  };
}
